/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { BuilderPermission } from '@gh/builder-shared-data';
import {
  FormStateSnapshot,
  getFormStateSnapshotValue,
  isEntityInvalid,
  isFieldInvalid,
  PlainImmutablePageState,
  UserContext,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { FormMode } from '@gh/core-ui';
import { switchMap$ } from '@gh/rx/operators';
import { find } from 'lodash';
import {
  CONSTRUCTION_WORKSHEET_DETAILS_INIT,
  ConstructionWorksheetDetails,
  ConstructionWorksheetMasterInfo,
  ConstructionWorksheetTask,
  constructionWorksheetTaskArr,
  getConstructionWorksheetTaskId,
  hasContructionWorksheetJob,
  isConstructionWorksheetJobEditable,
  setConstructionWorksheetTaskField,
} from '../../resources/construction-worksheet.model';
import { isSimpleValueEqual } from '@gh/core-util';

@Injectable()
export class ConstructionWorksheetFormState extends PlainImmutablePageState<ConstructionWorksheetDetails> {
  masterInfo$ = this.select(({ masterInfo }) => masterInfo);
  showCompletedTasks$ = this.select(({ showCompletedTasks }) => showCompletedTasks);
  tasks$ = this.showCompletedTasks$.pipe(switchMap$((showCompletedTasks) =>
    // tslint:disable-next-line:no-magic-numbers
    this.select(({ tasks }) => {
      const filteredTasks = tasks.filter((task) => !task.markedAsDeleted);
      // tslint:disable-next-line:no-magic-numbers
      return showCompletedTasks ? filteredTasks : filteredTasks.filter((task) => (task.completionPct || 0) < 100);
    })));
  focusedCell$ = this.select(({ focusedCell }) => focusedCell);
  history: ConstructionWorksheetDetails[] = [];
  historyPosition: number = 0;
  historyLength = 13;
  constructor(private userContext: UserContext) {
    super(CONSTRUCTION_WORKSHEET_DETAILS_INIT, FormMode.ViewMode);
  }

  get jobId(): ID {
    return getFormStateSnapshotValue(this.value.masterInfo).jobId;
  }

  get invalid(): boolean {
    return this.value.tasks.some(isEntityInvalid);
  }

  get undoDisabled(): boolean {
    return !this.history.length || this.history.length === 1 || this.historyPosition === 0;
  }

  get redoDisabled(): boolean {
    return !this.history.length || this.historyPosition === this.history.length - 1;
  }

  build(details: ConstructionWorksheetDetails): void {
    this.setMode(this.userContext.hasPermission(BuilderPermission.Schedules.Construction.EDIT)
    && isConstructionWorksheetJobEditable(details) && hasContructionWorksheetJob(details)
      ? FormMode.EditMode
      : FormMode.ViewMode);
    this.setValue(details);
    this.resetDirty();
    this.history = [];
    this.addToHistory(details);
  }

  undo(): void {
    this.historyPosition = this.historyPosition - 1;
    this.setValue(this.history[this.historyPosition]);
  }

  redo(): void {
    this.historyPosition++;
    this.setValue(this.history[this.historyPosition]);
  }

  setMasterInfo(info: FormStateSnapshot<ConstructionWorksheetMasterInfo>): void {
    this.setField('masterInfo', info);
  }

  setShowCompletedTasks(showCompletedTasks: boolean): void {
    this.setField('showCompletedTasks', showCompletedTasks);
  }

  addToHistory(value: ConstructionWorksheetDetails): void {
    if (this.historyPosition !== this.history.length - 1) {
      this.history.splice(this.historyPosition + 1);
    }
    if (this.history.length < this.historyLength) {
      this.history.push(value);
    } else {
      this.history.shift();
      this.history.push(value);
    }
    this.historyPosition = this.history.length - 1;
  }

  setTaskField(constructionWorksheetTaskId: ID, name: string, value: any): void {
    const targetTask = this.value.tasks.find((worksheetTask) => worksheetTask.scheduleTaskId === constructionWorksheetTaskId);
    const isValueIdentical = targetTask && isSimpleValueEqual(targetTask[name], value);
    this.setFieldWithDirty(
      'tasks',
      constructionWorksheetTaskArr.update(
        this.value.tasks,
        constructionWorksheetTaskId,
        (task) => setConstructionWorksheetTaskField(task, name, value)));
    if (!isValueIdentical) {
      this.addToHistory(this.value);
    }
  }

  focusFirstInvalidTask(): void {
    const invalidTask = find<ConstructionWorksheetTask>(this.value.tasks, isEntityInvalid);
    if (invalidTask) {
      if (isFieldInvalid(invalidTask, 'confirmedOnDate')) {
        this.setField('focusedCell', { id: getConstructionWorksheetTaskId(invalidTask), field: 'confirmedOnDate' });
      } else if (isFieldInvalid(invalidTask, 'confirmedForDate')) {
        this.setField('focusedCell', { id: getConstructionWorksheetTaskId(invalidTask), field: 'confirmedForDate' });
      }
    }
  }

  resetFocusedCell(): void {
    this.setField('focusedCell', void 0);
  }
}
